import React, { useState } from 'react';
import { TailSpin } from 'react-loader-spinner'; // Importing TailSpin loader

// Reusable MetricCard with hover tooltip and optional radial progress
function MetricCard({ label, value, icon, description, isLeakage }) {
  const [showTooltip, setShowTooltip] = useState(false);

  // Convert "XX%" string to number for radial progress. E.g., "23.5%" => 23.5
  const numericValue = parseFloat(value);

  // For radial progress using conic-gradient (only used when isLeakage is true)
  const circleStyle = {
    background: `conic-gradient(
      #E5252E ${numericValue * 3.6}deg,
      #e5e7eb ${numericValue * 3.6}deg
    )`,
  };

  return (
    <div
      className="relative p-4 bg-white/70 backdrop-blur-sm rounded-xl shadow-lg border border-transparent hover:border-[#E5252E] transition-all duration-300 cursor-pointer overflow-hidden"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Tooltip */}
      {showTooltip && description && (
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10 opacity-90">
          {description}
        </div>
      )}

      <div className="flex items-center space-x-4">
        {/* Icon or Radial Progress */}
        {isLeakage ? (
          <div className="w-16 h-16 flex items-center justify-center relative">
            <div className="w-16 h-16 rounded-full" style={circleStyle}></div>
            <div className="absolute inset-0 flex items-center justify-center font-bold text-white text-sm">
              {value}
            </div>
          </div>
        ) : (
          <div className="p-3 bg-[#19294F] rounded-full">
            {icon}
          </div>
        )}

        {/* Label & Value */}
        <div>
          <p className="text-sm text-[#707070]">{label}</p>
          {!isLeakage && (
            <p className="text-xl font-bold text-[#19294F]">{value}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function LeakageSummaryCard({ summaryData }) {
  const [expanded, setExpanded] = useState(true); // Default is expanded

  if (!summaryData) {
    return (
      <div className="bg-white shadow-lg rounded-xl p-6 mb-6">
        <div className="flex justify-center items-center">
          <TailSpin height="50" width="50" color="blue" ariaLabel="loading" />
          <p className="text-blue-500 ml-4 text-lg animate-pulse">Loading data...</p>
        </div>
      </div>
    );
  }

  // Build metrics array based on available data
  const metrics = [
    ...(summaryData.scopeCount !== undefined
      ? [
          {
            key: 'scopeCount',
            label: 'Scope Count',
            value: summaryData.scopeCount,
            description: 'Total number of scopes being monitored',
            icon: (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V7 M3 7a2 2 0 012-2h3.5a2 2 0 012 2h5a2 2 0 012 2H3z"
                />
              </svg>
            ),
            isLeakage: false,
          },
        ]
      : []),
    ...(summaryData.employeeCount !== undefined
      ? [
          {
            key: 'employeeCount',
            label: 'Employees',
            value: summaryData.employeeCount,
            description: 'Number of employees in scope',
            icon: (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a4 4 0 00-4-4h-1 M9 20H4v-2a4 4 0 014-4h1 m8-6a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            ),
            isLeakage: false,
          },
        ]
      : []),
    {
      key: 'productiveHours',
      label: 'Productive Hours',
      value: summaryData.totalProductiveHours,
      description: 'Total productive hours logged',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6 text-white"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3" />
        </svg>
      ),
      isLeakage: false,
    },
    {
      key: 'toolEffectiveness',
      label: 'Tool Effectiveness',
      value: summaryData.toolEffectiveness,
      description: 'Most used tool effectiveness',
      icon: (
        // Star icon for most used
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6 text-white"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01z" />
        </svg>
      ),
      isLeakage: false,
    },
    {
      key: 'leakageHours',
      label: 'Leakage Hours',
      value: summaryData.totalLeakageHours,
      description: 'Total hours lost to leakage',
      icon: (
        // Hourglass icon for leakage hours
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6 text-white"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 2l4 8-4 8M16 2l-4 8 4 8M6 2h12M6 22h12"
          />
        </svg>
      ),
      isLeakage: false,
    },
    {
      key: 'leakagePercentage',
      label: 'Leakage %',
      value: summaryData.totalLeakagePercentage,
      description: 'Leakage percentage vs. total hours',
      icon: null, // Radial progress will be used instead
      isLeakage: true,
    },
  ];

  return (
    <div className="bg-blue-100 shadow-2xl rounded-xl p-6 border-l-8 border-red-500 transition-all duration-300 mb-6 ">
      {/* Header with Expand/Collapse Toggle */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-2">
          <h2 className="text-2xl font-bold text-gray-800">Leakage Summary</h2>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-[#19294F] hover:text-[#E5252E] transition-transform transform"
        >
          <span className={`inline-block transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </span>
        </button>
      </div>

      {/* Animated Container for Metric Cards */}
      <div className={`overflow-hidden transition-all duration-500 ${expanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mt-2">
          {metrics.map((metric) => (
            <MetricCard
              key={metric.key}
              label={metric.label}
              value={metric.value}
              icon={metric.icon}
              description={metric.description}
              isLeakage={metric.isLeakage}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default LeakageSummaryCard;
